import { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router';
import {
  Box, Sheet, Typography, FormControl, FormLabel, Input, Button,
  Alert, Divider, Tabs, TabList, Tab, CircularProgress,
} from '@mui/joy';
import { LayoutContext } from '../context';
import {
  apiPatientRegister, apiPatientRegisterRequestCode,
  apiPatientRegisterVerifyCode,
} from '../auth';
import { formatPhoneRu, phoneDigitsCount } from '../utils';
import ConsentCheckbox from '../components/auth/ConsentCheckbox';

const CODE_LENGTH = { email: 6, call: 4 };

export default function RegisterPatientPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { applyLogin } = useContext(LayoutContext);
  const [step, setStep] = useState('find'); // find | channel | code
  const [mode, setMode] = useState('phone');
  const [contact, setContact] = useState('');
  const [doctorToken, setDoctorToken] = useState('');
  const [ticket, setTicket] = useState('');
  const [channels, setChannels] = useState([]);
  const [channel, setChannel] = useState('');
  const [masked, setMasked] = useState('');
  const [code, setCode] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [error, setError] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);
  const changeConsent = (value) => {
    setConsentAccepted(value);
    setError((previous) => previous === 'Подтвердите согласие на обработку персональных данных' ? '' : previous);
  };
  const [patientFound, setPatientFound] = useState(true);
  const [reviewRequired, setReviewRequired] = useState(false);
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setDoctorToken(params.get('doctor_token') || params.get('start') || '');
  }, [location.search]);

  useEffect(() => {
    if (!secondsLeft) return;
    const id = window.setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(id);
  }, [secondsLeft]);

  const changeMode = (_, next) => {
    if (!next) return;
    setMode(next);
    setContact('');
    setError('');
  };

  const findPatient = async () => {
    setError('');
    if (loading) return;
    if (!consentAccepted) { setError('Подтвердите согласие на обработку персональных данных'); return; }
    const normalized = contact.trim();
    if (mode === 'phone' ? phoneDigitsCount(normalized) !== 11
        : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
      setError(mode === 'phone' ? 'Введите телефон полностью' : 'Введите корректную почту');
      return;
    }
    setLoading(true);
    try {
      const result = await apiPatientRegister({ mode, contact: normalized,
        consentAccepted, doctor_token: doctorToken || undefined });
      setTicket(result.ticket);
      setPatientFound(result.patient_found);
      setReviewRequired(result.review_required === true);
      setFullName('');
      setChannels(result.channels || []);
      // Автовыбор того же способа, которым пациента нашли, если он есть в карточке МИС.
      const preferred = mode === 'email' ? 'email' : 'call';
      setChannel(result.channels?.some((item) => item.channel === preferred)
        ? preferred : (result.channels?.[0]?.channel || ''));
      setStep('channel');
    } catch (e) {
      setError(e?.response?.data?.error || 'Не удалось проверить данные. Попробуйте позже.');
    } finally { setLoading(false); }
  };

  const sendCode = async () => {
    setError('');
    if (loading) return;
    if (!consentAccepted) { setError('Подтвердите согласие на обработку персональных данных'); return; }
    if (!patientFound && fullName.trim().length < 3) { setError('Укажите ФИО'); return; }
    if (!ticket || !channel) { setError('Выберите способ подтверждения'); return; }
    setLoading(true);
    try {
      const result = await apiPatientRegisterRequestCode({ ticket, channel, consentAccepted });
      setMasked(result.destination_masked || '');
      setSecondsLeft(60);
      setCode('');
      setStep('code');
    } catch (e) {
      setError(e?.response?.data?.error || 'Не удалось отправить код');
    } finally { setLoading(false); }
  };

  const verify = async () => {
    setError('');
    if (loading) return;
    if (!consentAccepted) { setError('Подтвердите согласие на обработку персональных данных'); return; }
    const length = CODE_LENGTH[channel] || 6;
    if (code.length !== length) { setError(`Введите ${length} цифр`); return; }
    setLoading(true);
    try {
      const result = await apiPatientRegisterVerifyCode({ ticket, channel, code,
        device_name: navigator.userAgent, consentAccepted, name: patientFound ? undefined : fullName.trim() });
      applyLogin(result.profile);
      navigate('/cabinet', { replace: true });
    } catch (e) {
      setError(e?.response?.data?.error || 'Не удалось подтвердить код');
    } finally { setLoading(false); }
  };

  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', p: 2, bgcolor: 'background.level1' }}>
      <Sheet variant="outlined" sx={{ width: '100%', maxWidth: 440, p: { xs: 2.5, sm: 4 },
        borderRadius: 'lg', boxShadow: 'md', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Typography level="h3">Регистрация</Typography>
          <Typography level="body-sm" sx={{ mt: 0.5 }} textColor="text.tertiary">
            {doctorToken ? 'Вход по приглашению врача' : 'Личный кабинет пациента АЛВИ'}
          </Typography>
        </Box>
        {error && <Alert color="danger" variant="soft">{error}</Alert>}
        {loading && <CircularProgress size="sm" sx={{ alignSelf: 'center' }} />}

        {step === 'find' && <>
          <Typography level="body-sm">Проверим вашу карточку в клинике. Если её ещё нет, создадим заявку после подтверждения контакта.</Typography>
          <Tabs value={mode} onChange={changeMode} sx={{ bgcolor: 'transparent' }}>
            <TabList sx={{ display: 'flex', '& button': { flex: 1 } }}>
              <Tab value="phone">Телефон</Tab><Tab value="email">Почта</Tab>
            </TabList>
          </Tabs>
          <FormControl>
            <FormLabel>{mode === 'phone' ? 'Телефон, указанный в карточке пациента' : 'Почта, указанная в карточке пациента'}</FormLabel>
            {mode === 'phone' ? <Input type="tel" placeholder="+7 (999) 123-45-67"
              value={contact} onChange={(e) => setContact(formatPhoneRu(e.target.value, contact))}
              slotProps={{ input: { inputMode: 'tel', maxLength: 18 } }}
              onKeyDown={(e) => e.key === 'Enter' && findPatient()} />
              : <Input type="email" placeholder="you@mail.ru" value={contact}
                onChange={(e) => setContact(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && findPatient()} />}
          </FormControl>
          <Button loading={loading} disabled={!consentAccepted} onClick={findPatient}>Далее</Button>
          <ConsentCheckbox checked={consentAccepted} onChange={changeConsent} disabled={loading} />
        </>}

        {step === 'channel' && <>
          <Typography level="title-md">Подтвердите, что это вы</Typography>
          {!patientFound && <FormControl><FormLabel>ФИО</FormLabel>
            <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Фамилия Имя Отчество"
              slotProps={{ input: { maxLength: 255 } }} />
          </FormControl>}
          <Typography level="body-sm" textColor="text.tertiary">
            {reviewRequired
              ? 'На этот контакт найдено несколько карточек пациентов. Укажите свои ФИО и подтвердите контакт. После этого заявка будет передана администратору для уточнения карточки.'
              : patientFound
                ? 'Выберите контакт из карточки пациента. Другую почту или номер указать нельзя.'
                : 'Подтвердите указанный контакт. После этого откроется кабинет с заявкой на одобрение.'}
          </Typography>
          <Tabs value={channel} onChange={(_, value) => value && setChannel(value)}
            sx={{ bgcolor: 'transparent' }}>
            <TabList sx={{ display: 'flex', '& button': { flex: 1 } }}>
              {channels.map((item) => <Tab key={item.channel} value={item.channel}>
                {item.channel === 'email' ? `Почта ${item.destination_masked}`
                  : `Звонок ${item.destination_masked}`}
              </Tab>)}
            </TabList>
          </Tabs>
          <Button loading={loading} disabled={!consentAccepted || !channel || (!patientFound && fullName.trim().length < 3)} onClick={sendCode}>Продолжить</Button>
          <ConsentCheckbox checked={consentAccepted} onChange={changeConsent} disabled={loading} />
          <Button variant="plain" onClick={() => { setStep('find'); setTicket(''); setError(''); }}>
            Изменить данные поиска
          </Button>
        </>}

        {step === 'code' && <>
          <Typography level="body-sm" textColor="text.tertiary">
            {channel === 'call' ? `Вам поступит звонок на ${masked}. Введите последние 4 цифры номера звонящего.`
              : `Код отправлен на ${masked}.`}
          </Typography>
          <FormControl>
            <FormLabel>Код подтверждения</FormLabel>
            <Input autoFocus value={code} placeholder={'•'.repeat(CODE_LENGTH[channel] || 6)}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH[channel] || 6))}
              onKeyDown={(e) => e.key === 'Enter' && verify()}
              slotProps={{ input: { inputMode: 'numeric', maxLength: CODE_LENGTH[channel] || 6 } }} />
          </FormControl>
          <Button loading={loading} disabled={!consentAccepted} onClick={verify}>Подтвердить и войти</Button>
          <ConsentCheckbox checked={consentAccepted} onChange={changeConsent} disabled={loading} />
          <Button variant="plain" disabled={!consentAccepted || secondsLeft > 0 || loading} onClick={sendCode}>
            {secondsLeft ? `Отправить повторно (${secondsLeft})` : 'Отправить повторно'}
          </Button>
          <Button variant="plain" onClick={() => { setStep('channel'); setCode(''); setError(''); }}>
            Другой способ подтверждения
          </Button>
        </>}

        <Divider>уже есть аккаунт?</Divider>
        <Button variant="soft" component={Link} to="/login/patient">Войти</Button>
        <Typography level="body-xs" textColor="text.tertiary" sx={{ textAlign: 'center' }}>
          Если у вас иностранный номер, выберите регистрацию по электронной почте.
        </Typography>
      </Sheet>
    </Box>
  );
}